import { cn } from '@/lib/utils'
import type { CampaignStatus } from '@/lib/api'

type Size = 'sm' | 'md'

type StatusEntry = {
  dot: string
  label: string
  container: string
}

// Couleurs alignées sur ux-design-specification.md lignes 555-570 (Rose-Violet-Slate).
// Chaque status campagne a un label FR et un conteneur tonal distinct du
// `SupplierStatusBadgeV2` pour éviter la confusion entre « statut de la
// campagne » et « statut de la soumission fournisseur ».
const STATUS_CONFIG: Record<CampaignStatus, StatusEntry> = {
  DRAFT: {
    dot: 'bg-slate-400',
    label: 'Brouillon',
    container: 'bg-slate-100 text-slate-700',
  },
  SCHEDULED: {
    dot: 'bg-sky-400',
    label: 'Programmée',
    container: 'bg-sky-50 text-sky-700',
  },
  ACTIVE: {
    dot: 'bg-emerald-500',
    label: 'Active',
    container: 'bg-emerald-100 text-emerald-800',
  },
  CLOSED: {
    dot: 'bg-violet-600',
    label: 'Clôturée',
    container: 'bg-violet-100 text-violet-800',
  },
  ARCHIVED: {
    dot: 'bg-slate-500',
    label: 'Archivée',
    container: 'bg-slate-200 text-slate-700',
  },
}

// Fallback neutre : protège le rendu si le back renvoie un statut inconnu
// (ex. nouvelle enum côté server-side avant redeploy frontend).
const FALLBACK_STATUS: StatusEntry = {
  dot: 'bg-slate-400',
  label: 'Inconnu',
  container: 'bg-slate-100 text-slate-700',
}

export function CampaignStatusBadge({
  status,
  size = 'sm',
}: {
  status: CampaignStatus
  size?: Size
}) {
  const config = STATUS_CONFIG[status] ?? FALLBACK_STATUS
  const sizeClasses =
    size === 'md' ? 'px-3 py-1 text-sm' : 'px-2.5 py-0.5 text-xs'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        sizeClasses,
        config.container
      )}
      role="status"
      aria-label={`Statut campagne: ${config.label}`}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', config.dot)} aria-hidden="true" />
      {config.label}
    </span>
  )
}
